import { Pagination } from "services/search";

import {
  FleetDocumentUpload,
  FleetDocument,
  FleetDocumentEditFormValues,
  FleetDocumentRow,
  FleetDocumentType,
} from "./types";

import { DataWithPagination } from "services/api/types";

import { createApiCall, paginatedApiCall } from "services/api/createApiCall";
import {
  transformFleetDocument,
  transformFleetDocumentOnCreate,
  transformFleetDocumentOnUpdate,
  transformFleetDocumentRow,
  transformFleetDocumentType,
  transformFleetDocumentUpload,
} from "./transformations";
import { getAccessToken } from "services/auth";
import { filesApi } from "services/api/config";
import { transformErrorResponse } from "services/api/transformation";

/*---------------------------------------------------------------------------------------
  SEARCH
-----------------------------------------------------------------------------------------*/

export const getFleetDocumentsAPI = async (
  pagination: Pagination,
  name?: string | null,
  entityId?: string,
  entityName?: string,
  documentTypeId?: string,
  /** Razdoblje unosa; ostali filtri ostaju zbog postojećih poziva. */
  createdFrom?: Date | null,
  createdTo?: Date | null
): Promise<DataWithPagination<FleetDocumentRow>> => {
  let path = `/v1/fleet/documents?name=`;

  if (name) path += `${encodeURIComponent(name)}`;
  if (entityId) path += `&entityId=${entityId}`;
  if (entityName) path += `&entityName=${entityName}`;
  if (documentTypeId) path += `&documentTypeId=${documentTypeId}`;
  if (createdFrom) path += `&createdFrom=${createdFrom.toISOString()}`;
  if (createdTo) path += `&createdTo=${createdTo.toISOString()}`;

  return await paginatedApiCall(
    path,
    pagination,
    transformFleetDocumentRow
  );
};

/*---------------------------------------------------------------------------------------
  INFO
-----------------------------------------------------------------------------------------*/

export const getFleetDocumentAPI = async (
  id: string
): Promise<FleetDocument> => {
  const response = await createApiCall(
    {
      path: `/v1/fleet/documents/${id}`,
      method: "GET",
    },
    {
      getMessage: {
        error: (error: any) => `${error.response.data.message}`,
      },
    }
  )();

  return transformFleetDocument(response.data);
};

/*---------------------------------------------------------------------------------------
  UPLOAD
-----------------------------------------------------------------------------------------*/

export const uploadFleetDocumentAPI = async (
  files: File[]
): Promise<FleetDocumentUpload[]> => {
  const config = {
    headers: {
      Authorization: `Bearer ${getAccessToken()}`,
      "Content-Type": "multipart/form-data",
    },
  };

  const uploads = files.map(async (file) => {
    const formData = new FormData();
    formData.append("file", file);

    const response = await filesApi.post(`/v1/fleet/upload/`, formData, config);
    return transformFleetDocumentUpload(response.data);
  });

  return Promise.all(uploads);
};

/*---------------------------------------------------------------------------------------
  CREATE
-----------------------------------------------------------------------------------------*/

export const createFleetDocumentAPI = async (
  entityId: string,
  entityName: string,
  formValues: FleetDocumentEditFormValues,
  uploads: FleetDocumentUpload[],
  t: (key: string) => string
): Promise<FleetDocument> => {
  const response = await createApiCall(
    {
      path: "/v1/fleet/documents/",
      method: "POST",
      body: transformFleetDocumentOnCreate(
        entityId,
        entityName,
        formValues,
        uploads
      ),
    },
    {
      getMessage: {
        success: () => t("admin.fleet.document.create.message.success"),
        error: (error: any) => `${error.response.data.message}`,
      },
    }
  )();

  return transformFleetDocument(response.data);
};

/*---------------------------------------------------------------------------------------
    UPDATE
-----------------------------------------------------------------------------------------*/

export const updateFleetDocumentAPI = async (
  entityId: string,
  entityName: string,
  documentId: string,
  formValues: FleetDocumentEditFormValues,
  uploads: FleetDocumentUpload[],
  t: (key: string) => string
): Promise<FleetDocument> => {
  const response = await createApiCall(
    {
      path: `/v1/fleet/documents/${documentId}`,
      method: "PUT",
      body: transformFleetDocumentOnUpdate(entityId, entityName, formValues, uploads),
    },
    {
      getMessage: {
        success: () => t("admin.fleet.vehicle.document.update.message.success"),
        error: (error: any) => `${error.response.data.message}`,
      },
    }
  )();

  return transformFleetDocument(response.data);
};

/*---------------------------------------------------------------------------------------
    DELETE
-----------------------------------------------------------------------------------------*/

export const deleteFleetDocumentAPI = async (
  id: string,
  t: (key: string) => string
): Promise<FleetDocument> => {
  const response = await createApiCall(
    {
      path: `/v1/fleet/documents/${id}`,
      method: "DELETE",
    },
    {
      getMessage: {
        success: () => t("admin.fleet.vehicle.document.message.delete"),
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();

  return response.data?.deleted;
};

/*---------------------------------------------------------------------------------------
    DELETE
-----------------------------------------------------------------------------------------*/

export const getFleetDocumentDownloadAPI = async (
  documentId: string
): Promise<Blob> => {

  const response = await createApiCall(
    {
      path: `/v1/fleet/documents/${documentId}/download`,
      method: "GET",
      responseType: "blob",
    },
    {
      getMessage: {
        error: (error: any) => `${error.response.data.message}`,
      },
    }
  )();

  const contentType = response.headers["content-type"] ?? "application/octet-stream";

  return new Blob([response.data], { type: contentType });
};

/*--------------------------------------------------------------------------------
  DOCUMENT TYPES
----------------------------------------------------------------------------------*/

export const getFleetDocumentTypesAPI = async (
  name?: string,
  isForTireSet?: boolean,
): Promise<
  FleetDocumentType[]
> => {

  let path = `/v1/fleet/documents/types?name=`;
  if (name) path += `${name}`;
  if (isForTireSet) path += `&isForTireSet=${isForTireSet}`;

  const response = await createApiCall(
    {
      path: path,
      method: "GET",
    },
    {
      getMessage: {
        error: (error: any) => `${error.response.data.message}`,
      },
    }
  )();

  return response ? response.data.map(transformFleetDocumentType) : [];
};


/*---------------------------------------------------------------------------------------
    DELETE
-----------------------------------------------------------------------------------------*/

export const deleteFleetDocumentUploadAPI = async (
  documentId: string,
  uploadId: string,
  t: (key: string) => string
): Promise<FleetDocumentUpload> => {
  const response = await createApiCall(
    {
      path: `/v1/fleet/documents/${documentId}/upload/${uploadId}`,
      method: "DELETE",
    },
    {
      getMessage: {
        success: () => t("admin.fleet.vehicle.document.upload.message.delete"),
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();

  return response.data?.deleted;
};